import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Spinner, Form, Alert } from 'react-bootstrap';
import { Copy, Share, Sms } from 'iconsax-react';
import MyApiClient from '../../../api-client/MyApiClient';
import { useFunzioni } from '../Util/featureFlags';
import { SelettoreStato } from '../Util/StatoProdotto';
import { segnalaMancanti } from '../Util/campiMancanti';
import { chiediConferma } from '../Util/chiediConferma';

// Link d'acquisto di un prodotto (corso, abbonamento, lezione di un maestro, impianto): la struttura lo manda via email o WhatsApp e il cliente
// compra dal web player, anche senza un account SportID (si registra durante l'acquisto).
// Un prodotto può avere più link, uno per canale o destinatario («Newsletter di ottobre», «Gruppo WhatsApp under 12»): ognuno ha
// la sua nota, le sue viste e le sue vendite, e si può disattivare. Dalla scheda prodotto si vedono i link del prodotto e se ne
// crea uno nuovo; la pagina Link li elenca tutti.

const token = () => JSON.parse(sessionStorage.getItem('userDetails')).idToken;

const copia = async (testo) => {
    try {
        await navigator.clipboard.writeText(testo);
        return true;
    } catch {
        // Browser senza permesso per gli appunti: si seleziona il testo e si usa il comando classico.
        const area = document.createElement('textarea');
        area.value = testo;
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(area);
        return ok;
    }
};

export const STATI_LINK = { ACTIVE: 'Attivo', DISABLED: 'Non attivo' };

/**
 * Lo stato che conta per chi apre il link: Non attivo se l'ha disattivato la struttura oppure se il prodotto
 * non è attivo (in bozza, non attivo, terminato); in quel caso torna Attivo da solo quando si riattiva il prodotto.
 */
export const statoEffettivo = (link) =>
    link?.status === 'DISABLED' || link?.productPurchasable === false ? 'DISABLED' : 'ACTIVE';

export const prezzo = (valore) =>
    typeof valore === 'number' ? valore.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }) : '';

/**
 * La finestra di un link: in alto la condivisione (copia, WhatsApp, email), poi titolo, nota, il link generato e,
 * se `conStato`, lo stato in una tendina che si salva subito; in fondo Chiudi e Salva (titolo e nota).
 * `link` è quello del backend: { token, url, title, note, status }. Senza `link` e con `nuovo` ({ productType, productId })
 * è un link da creare: il sistema lo genera al salvataggio e da lì si può condividere.
 * Salvando chiama `onSalvato(link, creato)` con il link aggiornato o appena creato.
 */
export const LinkShareModal = ({ show, onHide, link, nuovo, nomeProdotto, acquistabile = true, conStato = false, onSalvato, onRimosso, onVediTutti }) => {
    const [titolo, setTitolo] = useState('');
    const [nota, setNota] = useState('');
    const [stato, setStato] = useState('ACTIVE');
    const [copiato, setCopiato] = useState(false);
    const [salvataggio, setSalvataggio] = useState(false);
    const [esito, setEsito] = useState(null);
    // Nella creazione: il link appena generato (sotto titolo e nota compaiono il link e la condivisione)
    const [generato, setGenerato] = useState(null);

    useEffect(() => {
        if (!show) return;
        setTitolo(link?.title || '');
        setNota(link?.note || '');
        setStato(link?.status || 'ACTIVE');
        setCopiato(false);
        setEsito(null);
        setGenerato(null);
        // Solo quando si apre un altro link: aggiornare lo stato non deve azzerare titolo e nota in corso di modifica
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, link?.token]);

    const url = link?.url || generato?.url || '';
    const messaggio = `Ecco il link per acquistare «${nomeProdotto || 'il prodotto'}»: ${url}`;
    const daCreare = !link;
    // Nella creazione il link e la condivisione compaiono solo dopo «Genera»
    const mostraLink = !daCreare || !!generato;
    const chiudi = () => onHide(generato ? { generato: true } : undefined);
    // Si elimina solo un link mai usato: con viste o vendite resta, e al massimo si disattiva
    const rimovibile = !daCreare && !(Number(link?.views) > 0) && !(Number(link?.sales) > 0);
    const cambiato = daCreare
        || titolo.trim() !== (link?.title || '')
        || (nota || '') !== (link?.note || '');

    // Lo stato si salva appena si sceglie nella tendina (regola SUI, docs/sportid-ui.md capitolo 8)
    const cambiaStato = async (nuovoStato) => {
        const risposta = await new MyApiClient().updatePurchaseLink(token(), link.token, { status: nuovoStato });
        if (risposta?.error) return { error: risposta.error };
        setStato(nuovoStato);
        if (onSalvato) onSalvato({ ...link, status: nuovoStato }, false);
        return {};
    };

    const salva = async () => {
        if (segnalaMancanti(!titolo.trim() ? [{ campo: 'link-titolo', messaggio: 'Manca il titolo: serve a riconoscere il link nella pagina Link' }] : []) > 0) return;
        setSalvataggio(true);
        setEsito(null);
        const api = new MyApiClient();
        if (daCreare) {
            const creato = await api.createPurchaseLink(token(), nuovo.productType, nuovo.productId, { title: titolo.trim(), note: nota.trim(), perTuttiIMaestri: !!nuovo.perTuttiIMaestri });
            setSalvataggio(false);
            if (creato?.error || !creato?.url) {
                setEsito({ tipo: 'danger', testo: creato?.error || 'Non è stato possibile creare il link d’acquisto.' });
                return;
            }
            setGenerato(creato);
            if (onSalvato) onSalvato(creato, true);
            return;
        }
        // Con il prodotto non attivo lo stato del link non si tocca: torna attivo da solo quando si riattiva il prodotto
        const dati = { title: titolo.trim(), note: nota };
        const risposta = await api.updatePurchaseLink(token(), link.token, dati);
        setSalvataggio(false);
        if (risposta?.error) {
            setEsito({ tipo: 'danger', testo: risposta.error });
            return;
        }
        if (onSalvato) onSalvato({ ...link, ...dati, ...risposta }, false);
        // Salvato: la finestra si chiude
        onHide();
    };

    const rimuovi = async () => {
        if (!(await chiediConferma({ titolo: 'Eliminare il link?', testo: 'Il link di acquisto viene eliminato e non funziona più.', conferma: 'Elimina' }))) return;
        setSalvataggio(true);
        setEsito(null);
        const risposta = await new MyApiClient().deletePurchaseLink(token(), link.token);
        setSalvataggio(false);
        if (risposta?.error) {
            const manca = [404, 405].includes(risposta.status) || risposta.status === undefined;
            setEsito({ tipo: 'danger', testo: manca ? 'Eliminare i link non è ancora possibile nel backend: per ora puoi metterlo Non attivo.' : risposta.error });
            return;
        }
        if (onRimosso) onRimosso(link);
        onHide();
    };

    return (
        <Modal className="fade sid-link-modal" show={show} onHide={chiudi} centered>
            <Modal.Header closeButton>
                <Modal.Title>{daCreare ? 'Nuovo link di acquisto' : 'Link di acquisto'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {conStato && !daCreare && (
                    <div className="mb-3">
                        <SelettoreStato
                            stati={[
                                { value: 'ACTIVE', label: STATI_LINK.ACTIVE, tono: 'attivo' },
                                { value: 'DISABLED', label: STATI_LINK.DISABLED, tono: 'non-attivo' },
                            ]}
                            valore={acquistabile ? stato : 'DISABLED'}
                            disabled={!acquistabile}
                            onCambia={cambiaStato}
                        />
                    </div>
                )}
                {!acquistabile && (
                    <Alert variant="warning" className="text-dark">
                        Il prodotto non è <strong>Attivo</strong>, quindi anche il link è <strong>Non attivo</strong>: chi lo apre vede il prodotto ma non può acquistarlo. Torna attivo quando riattivi il prodotto.
                    </Alert>
                )}
                <Form.Group>
                    <Form.Label htmlFor="link-titolo">Titolo *</Form.Label>
                    <Form.Control id="link-titolo" value={titolo} maxLength={100} readOnly={!!generato} onChange={(e) => setTitolo(e.target.value)} />
                </Form.Group>
                <Form.Group className="mt-3">
                    <Form.Label>Nota</Form.Label>
                    <Form.Control as="textarea" rows={3} value={nota} maxLength={500} readOnly={!!generato} onChange={(e) => setNota(e.target.value)} />
                </Form.Group>
                {mostraLink && (<>
                <Form.Group className="mt-3">
                    <Form.Label>Link</Form.Label>
                    <Form.Control readOnly value={url} onFocus={(e) => e.target.select()} />
                </Form.Group>
                <div className="sid-share-chips mt-3">
                    <button type="button" className="sid-share-chip" disabled={!url} onClick={async () => setCopiato(await copia(url))}><Copy size="12" /> {copiato ? 'Copiato!' : 'Copia link'}</button>
                    <a className={`sid-share-chip sid-share-chip--whatsapp ${url ? '' : 'disabled'}`} aria-disabled={!url} href={url ? `https://wa.me/?text=${encodeURIComponent(messaggio)}` : undefined} target="_blank" rel="noreferrer">
                        <i className="fab fa-whatsapp" /> WhatsApp
                    </a>
                    <a className={`sid-share-chip ${url ? '' : 'disabled'}`} aria-disabled={!url} href={url ? `mailto:?subject=${encodeURIComponent(`Acquista ${nomeProdotto || ''}`.trim())}&body=${encodeURIComponent(messaggio)}` : undefined}>
                        <Sms size="12" /> Email
                    </a>
                </div>
                </>)}
                {esito && <Alert variant={esito.tipo} className="text-dark mt-3 mb-0">{esito.testo}</Alert>}
            </Modal.Body>
            {/* Nuovo link: tasti al centro e nessuna riga sopra */}
            <Modal.Footer className={daCreare ? `sid-link-footer-nuovo${generato ? '' : ' justify-content-center'}` : undefined}>
                {/* Link già usato: al posto di Elimina, in basso a sinistra, il perché e cosa si può fare */}
                {!daCreare && !rimovibile && (
                    <span className="sid-link-non-rimovibile me-auto">
                        {/* Su due righe, così i tasti a destra restano sulla loro riga */}
                        Link già acquistato,<br />
                        solo disabilitabile.
                    </span>
                )}
                {rimovibile && (
                    <Button className="btn btn-danger btn-add me-auto" onClick={rimuovi} disabled={salvataggio}>Elimina</Button>
                )}
                {daCreare ? (generato ? (<>
                    {/* Dopo «Genera»: in basso a sinistra il collegamento alla pagina Link, Chiudi per ultimo */}
                    {onVediTutti && <button type="button" className="btn btn-link sid-link-vedi-tutti me-auto" onClick={onVediTutti}>Vedi tutti i link generati</button>}
                    <Button className="btn btn-primary btn-add" onClick={chiudi}>Chiudi</Button>
                </>) : (<>
                    <Button className="btn btn-light btn-add" onClick={chiudi}>Chiudi</Button>
                    <Button className="btn btn-primary btn-add" onClick={salva} disabled={salvataggio}>{salvataggio ? 'Generazione…' : 'Genera'}</Button>
                </>)) : (<>
                    <Button className="btn btn-light btn-add" onClick={chiudi}>Chiudi</Button>
                    <Button className="btn btn-primary btn-add" onClick={salva} disabled={salvataggio || !cambiato}>{salvataggio ? 'Salvataggio…' : 'Salva'}</Button>
                </>)}
            </Modal.Footer>
        </Modal>
    );
};

// Spento con REACT_APP_FEATURE_LINK_ACQUISTO diverso da S: il pulsante non compare.
export const LinkAcquisto = (props) => (useFunzioni().purchaseLinks ? <LinkAcquistoAttivo {...props} /> : null);

const dataBreve = (ms) => (ms ? new Date(ms).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

// `perTuttiIMaestri`: lezione con piu' maestri, i link nuovi valgono per tutto il gruppo e chi compra sceglie il maestro
const LinkAcquistoAttivo = ({ productType, productId, nomeProdotto, attivo, className = '', etichetta = 'Link di acquisto', spiegazione = true, perTuttiIMaestri = false }) => {
    const navigate = useNavigate();
    const [aperto, setAperto] = useState(false);
    const [links, setLinks] = useState(null);
    const [errore, setErrore] = useState('');
    // Il link aperto nella finestra: un link esistente, oppure { nuovo: true } per crearne uno.
    const [scelto, setScelto] = useState(null);
    const [copiato, setCopiato] = useState('');

    const apri = async () => {
        setAperto(true);
        setErrore('');
        setCopiato('');
        const risposta = await new MyApiClient().getPurchaseLinks(token(), { productType, productId });
        if (risposta?.error) {
            setErrore(risposta.error);
            setLinks([]);
            return;
        }
        setLinks(risposta);
        // Nessun link per un prodotto attivo: si va direttamente alla creazione
        if (attivo && Array.isArray(risposta) && risposta.length === 0) setScelto({ nuovo: true });
    };

    // Chiudendo la creazione si chiude tutto se il link è stato generato (per l'elenco c'è «Vedi tutti i link
    // generati») o se non ci sono link; altrimenti si torna all'elenco da cui si era partiti
    const chiudiLink = (opzioni) => {
        if (scelto?.nuovo && (opzioni?.generato || (links || []).length === 0)) setAperto(false);
        setScelto(null);
    };

    const salvato = (link, creato) => {
        setLinks((vecchi) => (creato ? [link, ...(vecchi || [])] : (vecchi || []).map((l) => (l.token === link.token ? { ...l, ...link } : l))));
        // Il link appena creato resta nella finestra di creazione, che mostra il link e la condivisione;
        // dopo la modifica di un link la finestra si chiude da sola (e si torna all'elenco)
    };

    return (
        <>
            {/* Un solo elemento cliccabile: icona, titolo e sottotitolo */}
            <button type="button" className={`sid-link-acquisto ${className}`} onClick={apri}>
                <span className="sid-link-acquisto__icon"><Share size={16} /></span>
                <span className="sid-link-acquisto__text">
                    <b>{etichetta}</b>
                    {spiegazione && <small>Condividilo: il cliente compra e paga online</small>}
                </span>
                <i className="fa fa-chevron-right sid-link-acquisto__arrow" aria-hidden="true" />
            </button>
            <Modal className="fade sid-link-modal" show={aperto && !scelto} onHide={() => setAperto(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Link di acquisto</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!attivo && (
                        <Alert variant="warning" className="text-dark">
                            Il prodotto non è <strong>Attivo</strong>: i suoi link sono <strong>Non attivi</strong> e non se ne possono creare di nuovi. Tornano attivi quando riattivi il prodotto.
                        </Alert>
                    )}
                    {errore && <Alert variant="danger" className="text-dark">{errore}</Alert>}
                    {links === null ? (
                        <div className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm" /> Caricamento dei link…</div>
                    ) : links.length === 0 ? (
                        // Con il prodotto non attivo basta l'avviso: niente messaggio di elenco vuoto
                        !errore && attivo && <p className="text-center mb-0">Non hai ancora creato link per questo prodotto.</p>
                    ) : (
                        <ul className="sid-link-list">
                            {links.map((l) => (
                                <li key={l.token} className={statoEffettivo(l) !== 'ACTIVE' ? 'is-disabled' : ''}>
                                    <button type="button" className="sid-link-list__main" onClick={() => setScelto(l)}>
                                        <b>{l.title || l.note || 'Senza titolo'}</b>
                                        <small>
                                            Creato il {dataBreve(l.createdAt)}{prezzo(l.productPrice) ? ` · ${prezzo(l.productPrice)}` : ''} · {Number(l.views) || 0} viste · {Number(l.sales) || 0} vendite
                                            {statoEffettivo(l) !== 'ACTIVE' ? ` · ${STATI_LINK[statoEffettivo(l)]}` : ''}
                                        </small>
                                    </button>
                                    <Button variant="outline-primary" size="sm" onClick={async () => setCopiato((await copia(l.url)) ? l.token : '')}>
                                        <Copy size="14" /> {copiato === l.token ? 'Copiato!' : 'Copia'}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Modal.Body>
                {/* Con il prodotto non attivo non si creano link: resta solo Chiudi, al centro */}
                <Modal.Footer className={attivo ? undefined : 'justify-content-center'}>
                    {/* Quando è l'unico tasto, Chiudi è pieno: chiaro sembrerebbe disabilitato */}
                    <Button className={`btn ${attivo ? 'btn-light' : 'btn-primary'} btn-add`} onClick={() => setAperto(false)}>Chiudi</Button>
                    {attivo && <Button className="btn btn-primary btn-add" onClick={() => setScelto({ nuovo: true })} disabled={links === null}>Crea nuovo link</Button>}
                </Modal.Footer>
            </Modal>
            <LinkShareModal show={aperto && !!scelto} onHide={chiudiLink} onVediTutti={() => { setScelto(null); setAperto(false); navigate('/struttura/link'); }} link={scelto?.nuovo ? null : scelto}
                nuovo={{ productType, productId, perTuttiIMaestri }} nomeProdotto={nomeProdotto} acquistabile={attivo} conStato onSalvato={salvato}
                onRimosso={(l) => setLinks((vecchi) => (vecchi || []).filter((v) => v.token !== l.token))} />
        </>
    );
};

export default LinkAcquisto;
